import useWindowSize from '@/common/hooks/useWindowSize'
import {
  Box,
  LayoutProps,
  List,
  ListItem,
  Portal,
  PositionProps,
  useColorMode,
} from '@chakra-ui/react'
import {
  GetPropsCommonOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetMenuPropsOptions,
} from 'downshift'
import React, { ReactNode, RefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FiPlus } from 'react-icons/fi'
import { SearchItem } from '../searchTypes'
import SearchResultButton from './SearchResultButton'
import { getScopedTreeTitles } from './searchResultUtils'

interface Props {
  items: SearchItem[]
  isOpen: boolean
  showCanCreate: boolean
  highlightedIndex: number
  getMenuProps: (
    options?: UseComboboxGetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions
  ) => any
  getItemProps: (options: UseComboboxGetItemPropsOptions<SearchItem>) => any
  inputRef: RefObject<HTMLInputElement>
  // ORG-02: a perimeter filter is active. Its results may miss their parents,
  // so each is indented under the parents listed in its group only (#123).
  flat?: boolean
  // ORG-06: optional footer action row (e.g. "Show roles too"), rendered
  // below the items. Not a search result: no getItemProps, so downshift never
  // intercepts it as a selection.
  bottomAction?: {
    label: string
    onClick(): void
  }
  // #123: the circle picker's filters, at the top of the list. Not a search
  // result either, and shown even when the filter lists nothing — it is then
  // the way to another filter.
  header?: ReactNode
}

const satisfyingWidth = 250
const satisfyingMinHeight = 250
const satisfyingMaxHeight = 500

export default function SearchResultsList({
  items,
  isOpen,
  showCanCreate,
  highlightedIndex,
  getMenuProps,
  getItemProps,
  inputRef,
  flat,
  bottomAction,
  header,
}: Props) {
  const { t } = useTranslation()
  const { colorMode } = useColorMode()
  const scopedTrees = useMemo(
    () => (flat ? getScopedTreeTitles(items) : undefined),
    [flat, items]
  )
  const windowSize = useWindowSize()

  const inputBounds = inputRef.current?.getBoundingClientRect()

  // Layout props
  const layoutProps: LayoutProps = {
    minW: `${inputBounds ? inputBounds.width : 0}px`,
    maxH: `${
      inputBounds
        ? Math.min(
            Math.max(
              windowSize.height - inputBounds.bottom - 15,
              satisfyingMinHeight
            ),
            satisfyingMaxHeight
          )
        : satisfyingMinHeight
    }px`,
  }

  // Position props
  const inputTop = inputBounds?.top || 0
  const inputBottom = inputBounds?.bottom || 0
  const positionProps: PositionProps =
    windowSize.height - inputBottom > Math.min(inputTop, 200)
      ? { top: `${inputBottom}px` }
      : { bottom: `${windowSize.height - inputTop}px` }

  // Determine alignment -> position + width
  if (inputBounds) {
    const alignRightWidth = inputBounds.right
    const alignLeftWidth = windowSize.width - inputBounds.left
    if (alignLeftWidth >= satisfyingWidth) {
      positionProps.left = inputBounds.left
      layoutProps.maxW = `${alignLeftWidth}px`
    } else if (alignRightWidth >= satisfyingWidth) {
      positionProps.right = windowSize.width - inputBounds.right
      layoutProps.maxW = `${alignRightWidth}px`
    } else {
      positionProps.left = 0
      layoutProps.w = '100vw'
    }
  }

  return (
    <Portal>
      <List
        {...getMenuProps(
          {},
          {
            // Ignore error occuring when using a portal
            suppressRefError: true,
          }
        )}
        display={
          // The footer counts as content: with no result in the first ring,
          // it is the only way to widen to the circle, then the org (#108).
          isOpen &&
          (items.length > 0 || bottomAction || header) &&
          inputRef.current?.offsetHeight
            ? ''
            : 'none'
        }
        position="fixed"
        overflow="auto"
        zIndex="2000"
        mt={1}
        shadow="md"
        bg={colorMode === 'light' ? 'white' : 'gray.700'}
        border="1px solid"
        borderColor="inherit"
        borderRadius="md"
        {...layoutProps}
        {...positionProps}
      >
        {header && <ListItem>{header}</ListItem>}

        {header && items.length === 0 && (
          <ListItem
            px={3}
            py={2}
            fontSize="sm"
            color="gray.500"
            _dark={{ color: 'gray.400' }}
          >
            {t('SearchResultsList.empty')}
          </ListItem>
        )}

        {showCanCreate && !items.some((item) => item.id === 'create') && (
          <ListItem px={2} py={1} fontSize="sm" fontStyle="italic">
            {t('SearchResultsList.canCreate')}
          </ListItem>
        )}

        {items.map((item, index) =>
          item.isSeparator ? (
            // ORG-02: soft perimeter partition separator, a non-selectable
            // label row (disabled in downshift, no click handler attached).
            <ListItem key={index}>
              <Box
                {...getItemProps({ item, index })}
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="bold"
                textTransform="uppercase"
                color="gray.500"
                _dark={{ color: 'gray.400' }}
                cursor="default"
                userSelect="none"
              >
                {item.title}
              </Box>
            </ListItem>
          ) : (
            <ListItem key={index}>
              <SearchResultButton
                {...getItemProps({ item, index })}
                item={item}
                prevItem={items[index - 1]}
                tree={scopedTrees?.[index]}
                highlighted={index === highlightedIndex}
                // #121: shown but not selectable, the reason on hover
                title={item.disabledReason}
                opacity={item.disabledReason ? 0.45 : undefined}
                cursor={item.disabledReason ? 'not-allowed' : undefined}
                size="sm"
                w="100%"
                h="auto"
                whiteSpace="break-spaces"
                borderRadius="none"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                _active={{
                  bg: colorMode === 'light' ? 'gray.100' : 'whiteAlpha.100',
                }}
              />
            </ListItem>
          )
        )}

        {bottomAction && (
          <ListItem borderTop="1px solid" borderColor="inherit">
            <Box
              as="button"
              type="button"
              display="flex"
              alignItems="center"
              gap={2}
              w="100%"
              px={3}
              py={2}
              fontSize="sm"
              textAlign="left"
              cursor="pointer"
              _hover={{
                bg: colorMode === 'light' ? 'gray.100' : 'whiteAlpha.100',
              }}
              // ORG-06: same parade as the filter bar (CirclePerimeterBar)
              // to keep the menu open on click.
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={bottomAction.onClick}
            >
              <FiPlus />
              {bottomAction.label}
            </Box>
          </ListItem>
        )}
      </List>
    </Portal>
  )
}
